import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Bell, MessageCircle, Menu, Sparkles, ChevronDown, Newspaper, User, LayoutGrid, FileText, Settings, UserCircle, ShieldCheck, Lock, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';
import { employees, appLinks, documents, knowledgeArticles, notifications as seedNotifications, Article } from './data';
import { useApp } from './store';
interface TopBarProps {
  onMenuClick: () => void;
}
interface SearchResult {
  type: 'News' | 'Person' | 'App' | 'Document';
  label: string;
  detail: string;
  to: string;
  icon: any;
}
function buildIndex(query: string, articles: Article[]): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results: SearchResult[] = [];
  articles.forEach(a => {
    if (a.title.toLowerCase().includes(q) || a.category.toLowerCase().includes(q)) results.push({
      type: 'News',
      label: a.title,
      detail: a.category,
      to: `/news/${a.id}`,
      icon: Newspaper
    });
  });
  employees.forEach(e => {
    if (e.name.toLowerCase().includes(q) || e.title.toLowerCase().includes(q)) results.push({
      type: 'Person',
      label: e.name,
      detail: e.title,
      to: `/people/${e.id}`,
      icon: User
    });
  });
  appLinks.forEach(a => {
    if (a.label.toLowerCase().includes(q)) results.push({
      type: 'App',
      label: a.label,
      detail: a.description || 'App',
      to: '/apps',
      icon: LayoutGrid
    });
  });
  [...documents, ...knowledgeArticles].forEach(d => {
    if (d.title.toLowerCase().includes(q)) results.push({
      type: 'Document',
      label: d.title,
      detail: d.category,
      to: knowledgeArticles.includes(d) ? '/knowledge' : '/documents',
      icon: FileText
    });
  });
  return results.slice(0, 8);
}
export function TopBar({
  onMenuClick
}: TopBarProps) {
  const navigate = useNavigate();
  const {
    user,
    isAdmin,
    lockAdmin,
    clearIdentity,
    articles
  } = useApp();
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifs, setNotifs] = useState(seedNotifications);
  const searchRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => buildIndex(query, articles), [query, articles]);
  const unreadCount = notifs.filter(n => n.unread).length;
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const goToResult = (to: string) => {
    setSearchOpen(false);
    setQuery('');
    navigate(to);
  };
  const markAllRead = () => setNotifs(n => n.map(x => ({
    ...x,
    unread: false
  })));
  return <header className="sticky top-0 z-30 bg-white border-b border-navy-100" data-id="element-974" data-mp="1xudwjwwiiru4">
      <div className="h-16 px-4 sm:px-6 flex items-center gap-4" data-id="element-975" data-mp="22uifdtkw7yni">
        <button className="lg:hidden p-2 text-navy-600 hover:bg-navy-50 rounded-lg" onClick={onMenuClick} aria-label="Open navigation" data-id="element-976" data-mp="22sxhbtzddxkw">
          <Menu className="w-6 h-6" data-id="element-977" data-mp="8ilivm7lg2ll" />
        </button>

        {/* Smart search */}
        <div className="flex-1 max-w-xl relative" ref={searchRef} data-id="element-978" data-mp="dgdmhdhlpd5m">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-navy-400 pointer-events-none" data-id="element-979" data-mp="23w64fcwe1pvo">
            <Search className="w-5 h-5" data-id="element-980" data-mp="1yxcprh5d7stx" />
          </div>
          <input type="text" value={query} onChange={e => {
          setQuery(e.target.value);
          setSearchOpen(true);
        }} onFocus={() => setSearchOpen(true)} placeholder="Search people, news, files, apps…" className="w-full pl-11 pr-24 py-2.5 rounded-full bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900 placeholder:text-navy-400 transition-all" data-id="element-981" data-mp="2cxdtd4d0led3" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[0.65rem] font-semibold text-gold-600 bg-gold-50 px-2 py-1 rounded-full uppercase tracking-wide pointer-events-none" data-id="element-982" data-mp="1p5yjmqiwrurt">
            <Sparkles className="w-3 h-3" data-id="element-983" data-mp="386r3mx7bqypg" />
            AI
          </span>

          <AnimatePresence data-id="element-984" data-mp="384mofj3z6d9v">
            {searchOpen && query.trim() && <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 6
          }} transition={{
            duration: 0.15
          }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-navy-100 shadow-xl overflow-hidden z-50" data-id="element-985" data-mp="3cz0ohytl18kv">
                {results.length === 0 ? <div className="p-6 text-center text-sm text-navy-400" data-id="element-986" data-mp="3mu2gd2f8sor5">
                    No results for “{query}”.
                  </div> : <ul className="max-h-96 overflow-y-auto py-2" data-id="element-987" data-mp="2bgqgf2mlsqa9">
                    {results.map((r, i) => {
                const Icon = r.icon;
                return <li key={i} data-id="element-988" data-mp="1nc8ri6ipgvfw" data-mpi={i}>
                          <button onClick={() => goToResult(r.to)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-navy-50 text-left transition-colors" data-id="element-989" data-mp="1ndaffbktlcf1">
                            <span className="w-9 h-9 shrink-0 rounded-lg bg-navy-50 flex items-center justify-center text-navy-500" data-id="element-990" data-mp="18nmeibgjwfru">
                              <Icon className="w-4 h-4" data-id="element-991" data-mp="13nzyju9c8lfc" />
                            </span>
                            <span className="min-w-0 flex-1" data-id="element-992" data-mp="zo7lb4901r3n">
                              <span className="block text-sm font-semibold text-navy-900 truncate" data-id="element-993" data-mp="uo37jlzh7ah4">
                                {r.label}
                              </span>
                              <span className="block text-xs text-navy-400 truncate" data-id="element-994" data-mp="2tz4hwxan9sfp">
                                {r.detail}
                              </span>
                            </span>
                            <span className="text-[0.6rem] font-bold uppercase tracking-wide text-navy-400" data-id="element-995" data-mp="2wjiz4xzamq7z">
                              {r.type}
                            </span>
                          </button>
                        </li>;
              })}
                  </ul>}
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 ml-auto" data-id="element-996" data-mp="2dp3yl3xc2pi1">
          <button onClick={() => toast('Direct messages are coming soon.')} className="relative p-2.5 text-navy-600 hover:bg-navy-50 rounded-full" aria-label="Messages" data-id="element-997" data-mp="2imdwz92pabi0">
            <MessageCircle className="w-5 h-5" data-id="element-998" data-mp="2rkfw1j2viu2n" />
          </button>

          {/* Notifications */}
          <div className="relative" ref={notifRef} data-id="element-999" data-mp="2wsrm24hmk61">
            <button onClick={() => {
            setNotifOpen(v => !v);
            setProfileOpen(false);
          }} className="relative p-2.5 text-navy-600 hover:bg-navy-50 rounded-full" aria-label="Notifications" data-id="element-1000" data-mp="6va0aq3k42pk">
              <Bell className="w-5 h-5" data-id="element-1001" data-mp="12o6gd5mekfqo" />
              {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-gold-500 rounded-full ring-2 ring-white" data-id="element-1002" data-mp="xx81mah2bppj" />}
            </button>

            <AnimatePresence data-id="element-1003" data-mp="1bmry4yk1vqqe">
              {notifOpen && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 6
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-navy-100 shadow-xl overflow-hidden z-50" data-id="element-1004" data-mp="16pru0t6rgy1x">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-navy-50" data-id="element-1005" data-mp="22vldluh9ejjs">
                    <span className="font-serif font-bold text-navy-900" data-id="element-1006" data-mp="1o7gu4oadu3zy">
                      Notifications
                    </span>
                    {unreadCount > 0 && <button onClick={markAllRead} className="inline-flex items-center gap-1 text-xs font-semibold text-gold-600 hover:text-gold-700" data-id="element-1007" data-mp="1f6a9c30pa70w">
                        <Check className="w-3.5 h-3.5" data-id="element-1008" data-mp="158mcn7jjapg3" /> Mark all read
                      </button>}
                  </div>
                  <ul className="max-h-96 overflow-y-auto" data-id="element-1009" data-mp="1a7futd9rn4ml">
                    {notifs.map(n => <li key={n.id} data-id="element-1010" data-mp="2zmx6xviwebt9">
                        <Link to={n.to} onClick={() => setNotifOpen(false)} className={`flex items-start gap-3 px-4 py-3 hover:bg-navy-50 transition-colors ${n.unread ? 'bg-gold-50/50' : ''}`} data-id="element-1011" data-mp="34kyp8e8i273o">
                          <img src={n.avatar} alt="" className="w-9 h-9 rounded-full shrink-0" data-id="element-1012" data-mp="1n8c7iotgrys4" />
                          <span className="min-w-0" data-id="element-1013" data-mp="2fw831q7arknb">
                            <span className="block text-sm text-navy-800 leading-snug" data-id="element-1014" data-mp="2fun65txifpma">
                              {n.text}
                            </span>
                            <span className="block text-xs text-navy-400 mt-0.5" data-id="element-1015" data-mp="25xiifpo0nm3d">
                              {n.time}
                            </span>
                          </span>
                          {n.unread && <span className="w-2 h-2 rounded-full bg-gold-500 mt-1.5 shrink-0" data-id="element-1016" data-mp="ui3mmp7jn09u" />}
                        </Link>
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Profile */}
          <div className="relative" ref={profileRef} data-id="element-1017" data-mp="10zetplhhclb4">
            <button onClick={() => {
            setProfileOpen(v => !v);
            setNotifOpen(false);
          }} className="flex items-center gap-2 pl-1.5 pr-2 sm:pr-3 py-1.5 rounded-full hover:bg-navy-50 transition-colors ml-1" data-id="element-1018" data-mp="10yvon5cxhrei">
              <img src={user.avatar} alt="Your profile" className="w-8 h-8 rounded-full object-cover ring-2 ring-gold-400" data-id="element-1019" data-mp="4az45pedeby0" />
              <span className="hidden sm:block text-sm font-semibold text-navy-900" data-id="element-1020" data-mp="d9as1jnuqvmr">
                {user.name}
              </span>
              <ChevronDown className={`hidden sm:block w-4 h-4 text-navy-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} data-id="element-1021" data-mp="3lnt3lvd04tb4" />
            </button>

            <AnimatePresence data-id="element-1022" data-mp="1kneiuozdth7z">
              {profileOpen && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 6
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-navy-100 shadow-xl overflow-hidden z-50" data-id="element-1023" data-mp="1plpr4va13jo3">
                  <div className="px-4 py-3 border-b border-navy-50" data-id="element-1024" data-mp="1fp3a6hktpcrc">
                    <p className="text-sm font-bold text-navy-900" data-id="element-1025" data-mp="2n25b5oo37l2v">
                      {user.name}
                    </p>
                    <p className="text-xs text-navy-400" data-id="element-1026" data-mp="2r3798269pr39">
                      {user.title} · {user.location}
                    </p>
                  </div>
                  <div className="py-1" data-id="element-1027" data-mp="fihk2usu80gw">
                    <Link to="/people/u3" onClick={() => setProfileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-1028" data-mp="bejmwpn2ozqh">
                      <UserCircle className="w-4 h-4 text-navy-400" data-id="element-1029" data-mp="1mw1b5d76cwx" /> My
                      Profile
                    </Link>
                    <Link to="/settings" onClick={() => setProfileOpen(false)} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-1030" data-mp="3nwgg1wft7lue">
                      <Settings className="w-4 h-4 text-navy-400" data-id="element-1031" data-mp="3exlb0noi4hso" /> Settings
                    </Link>
                    <button onClick={() => {
                  setProfileOpen(false);
                  clearIdentity();
                  navigate('/');
                }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 text-left" data-id="element-1032" data-mp="1i15ovx9m7xmp">
                      <RefreshCw className="w-4 h-4 text-navy-400" data-id="element-1033" data-mp="rrvzc4eiku1" /> Switch
                      profile
                    </button>
                    <div className="my-1 border-t border-navy-50" data-id="element-1034" data-mp="5tqyykw8bfqg" />
                    {isAdmin ? <>
                        <Link to="/admin" onClick={() => setProfileOpen(false)} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-1035" data-mp="3rykxyi70qnzm">
                          <ShieldCheck className="w-4 h-4 text-navy-400" data-id="element-1036" data-mp="3i6ouerm5y5o0" /> Site
                          Admin
                        </Link>
                        <button onClick={() => {
                    lockAdmin();
                    setProfileOpen(false);
                    navigate('/');
                  }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 text-left" data-id="element-1037" data-mp="398ms88qp16z5">
                          <Lock className="w-4 h-4 text-navy-400" data-id="element-1038" data-mp="2ulejdekuxdqc" /> Lock admin
                        </button>
                      </> : <Link to="/admin" onClick={() => setProfileOpen(false)} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-1039" data-mp="38j2v9wwy5aj9">
                        <ShieldCheck className="w-4 h-4 text-navy-400" data-id="element-1040" data-mp="3dcntnq2gc6iz" /> Unlock
                        Site Admin
                      </Link>}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>;
}